import React from 'react';
import {
  Paper,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  InputAdornment,
  Select,
  MenuItem,
  Button,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Clear as ClearIcon,
  FilterAltOutlined as FilterIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import { TransactionFilters } from '../../../types/transaction';
import { PaymentSystem } from '../../../types/common/payment';
import { Status } from '../../../types';
import SearchableSelect from '@/components/SearchableSelect';

interface TransactionFiltersProps {
  filters: TransactionFilters;
  merchants: any[];
  traders: any[];
  paymentSystems: PaymentSystem[];
  onFilterChange: (field: keyof TransactionFilters, value: any) => void;
  onSearch: () => void;
  onReset: () => void;
  onToggleIncludeNotFound: () => void;
}

const TransactionFiltersComponent: React.FC<TransactionFiltersProps> = ({
  filters,
  merchants,
  traders,
  paymentSystems,
  onFilterChange,
  onSearch,
  onReset,
  onToggleIncludeNotFound,
}) => {
  const startDateValue = filters.start_date ? new Date(filters.start_date) : null;
  const endDateValue = filters.end_date ? new Date(filters.end_date) : null;
  const includeNotFound = !!filters.include_not_found;
  const currencyCode = (system: PaymentSystem) => typeof system.currency === 'string'
    ? system.currency : system.currency?.code || system.currency?.iso_code || '';
  const currencies = [...new Set(paymentSystems.map(currencyCode).filter(Boolean))].sort();
  const visiblePaymentSystems = filters.currency
    ? paymentSystems.filter((system) => currencyCode(system) === filters.currency) : paymentSystems;
  const activeFiltersCount = [
    filters.merchant_id,
    filters.trader_id,
    filters.payment_system_id,
    filters.currency,
    filters.status,
    filters.start_date,
    filters.end_date,
    filters.search_id,
  ].filter(Boolean).length
    + (filters.is_sandbox !== undefined ? 1 : 0)
    + (filters.include_not_found ? 1 : 0);

  return (
    <Paper sx={{ p: { xs: 2, md: 2.5 }, border: 1, borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>Фильтры</Typography>
          {activeFiltersCount > 0 && (
            <Typography variant="body2" color="primary.main">
              Выбрано: {activeFiltersCount}
            </Typography>
          )}
        </Stack>
        <Button
          size="small"
          color="inherit"
          onClick={onReset}
          startIcon={<ClearIcon />}
          disabled={activeFiltersCount === 0}
          sx={{ color: 'text.secondary' }}
        >
          Очистить
        </Button>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
            xl: 'repeat(6, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
          <SearchableSelect
            label="Мерчант"
            value={filters.merchant_id}
            options={merchants.map((merchant) => ({ id: merchant.id, label: merchant.name }))}
            onChange={(value) => onFilterChange('merchant_id', value)}
          />
          <SearchableSelect
            label="Трейдер"
            value={filters.trader_id}
            options={traders.map((trader) => ({ id: trader.id, label: trader.name }))}
            onChange={(value) => onFilterChange('trader_id', value)}
          />
          <FormControl fullWidth>
            <InputLabel>Валюта</InputLabel>
            <Select value={filters.currency || ''} label="Валюта"
              onChange={(event) => onFilterChange('currency', event.target.value)}>
              <MenuItem value="">Все</MenuItem>
              {currencies.map((code) => <MenuItem key={code} value={code}>{code}</MenuItem>)}
            </Select>
          </FormControl>
          <SearchableSelect
            label="Платёжная система"
            value={filters.payment_system_id}
            options={visiblePaymentSystems.map((system) => ({ id: system.id, label: system.token }))}
            onChange={(value) => onFilterChange('payment_system_id', value)}
          />
          <FormControl fullWidth>
            <InputLabel>Статус</InputLabel>
            <Select
              value={filters.status || ''}
              label="Статус"
              onChange={(e) => onFilterChange('status', e.target.value)}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value={Status.SUCCESS}>Успешно</MenuItem>
              <MenuItem value={Status.ACCEPTED}>Ожидание</MenuItem>
              <MenuItem value={Status.DECLINED}>Отклонено</MenuItem>
              <MenuItem value={Status.APPEAL}>Апелляция</MenuItem>
              <MenuItem value={Status.NOT_FOUND}>Невыдача</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Режим</InputLabel>
            <Select
              value={filters.is_sandbox === undefined ? '' : String(filters.is_sandbox)}
              label="Режим"
              onChange={(e) => onFilterChange('is_sandbox', e.target.value === '' ? undefined : e.target.value === 'true')}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="false">Боевые</MenuItem>
              <MenuItem value="true">Sandbox</MenuItem>
            </Select>
          </FormControl>
      </Box>

      <Divider sx={{ my: 2 }} />

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'minmax(320px, 2fr) minmax(180px, 1fr) minmax(180px, 1fr) auto 150px',
            },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            label="ID / трекер / UTR / ID клиента"
            value={filters.search_id || ''}
            onChange={(e) => onFilterChange('search_id', e.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onSearch();
            }}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
            <DatePicker
              label="Начальная дата"
              value={startDateValue}
              onChange={(date) =>
                onFilterChange(
                  'start_date',
                  date ? date.toISOString().slice(0, 10) : undefined,
                )
              }
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="Конечная дата"
              value={endDateValue}
              onChange={(date) =>
                onFilterChange(
                  'end_date',
                  date ? date.toISOString().slice(0, 10) : undefined,
                )
              }
              slotProps={{ textField: { fullWidth: true } }}
            />
          <FormControlLabel
            control={<Switch checked={includeNotFound} onChange={onToggleIncludeNotFound} />}
            label="NOT_FOUND"
            sx={{
              m: 0,
              px: 1.25,
              minHeight: 56,
              border: 1,
              borderColor: includeNotFound ? 'primary.main' : 'divider',
              borderRadius: 1,
              whiteSpace: 'nowrap',
            }}
          />
          <Button
            variant="contained"
            onClick={onSearch}
            fullWidth
            startIcon={<SearchIcon />}
            sx={{ height: 56, boxShadow: 'none' }}
          >
            Применить
          </Button>
        </Box>
      </LocalizationProvider>
    </Paper>
  );
};

export default TransactionFiltersComponent; 
